import {
  G1N5,
  G1N6,
  KVAnnotation,
  KVBoundingBox,
  KVBoundingBoxJSON,
  KVCameraView,
  KVCameraViewJSON,
  KVCoordinate,
  KVDataLayersConfig,
  KVDataLayersConfigJSON,
  KVMeasurement,
  KVMeasurementJSON,
  KVPlaneIntersection,
  KVPlaneIntersectionJSON,
  KVPrimitive,
  KVSceneSettings,
  KVUnitType,
  normalizeKVUnit,
  RolodexEntryJson,
  WorldPosition,
} from 'types'
import { GeckoEntryBase } from 'types/entries/geckoEntry'
import { KestrelInspectionType } from 'types/kestrelViewer/inspectionType'
import { KVInspectionArea } from 'types/kestrelViewer/KVInspectionArea'
import { parseBoolean, parseString } from 'utils/parsingHelpers'

export interface KVAerospaceMetadata {
  missionDesignSeries?: string
  serialNumber?: string
}

export class GeckoKestrelModel extends GeckoEntryBase<
  {
    kestrel_model_id: string
    inspectionAreas: KVInspectionArea[]
    annotations: KVAnnotation[]
    measurements: KVMeasurement[]
    cameraViews: KVCameraView[]
    planeIntersections: KVPlaneIntersection[]
    primitives: KVPrimitive[]
    measurementScale: number
    photosEnabled: boolean
    g1n5: G1N5 | undefined
    g1n6: G1N6 | undefined
    inspection_type?: KestrelInspectionType
    sceneSettings: KVSceneSettings
    dataLayersConfig: KVDataLayersConfig
    underlyingAsset: string
    scanDate: number
    origin?: KVCoordinate
    boundingBox?: KVBoundingBox
    defaultUnit?: KVUnitType
    cameraPosesScale: number
    boundingSegments?: WorldPosition[]
    forms?: Record<string, string>
    photoRotations?: Record<string, number>
    aerospaceMetadata?: KVAerospaceMetadata
  },
  {
    organization: string
    site: string
    unit: string
    component: string
    camerasEntry: string
  }
> {
  private constructor(json: RolodexEntryJson) {
    const recordData = json.data as Record<string, unknown>

    const annotations = new Array<KVAnnotation>()
    const measurements = new Array<KVMeasurement>()
    const cameraViews = new Array<KVCameraView>()
    const planeIntersections = new Array<KVPlaneIntersection>()
    const inspectionAreas = new Array<KVInspectionArea>()
    const primitives = new Array<KVPrimitive>()
    const measurementScale = recordData?.measurementScale as number | 1
    const origin = recordData?.origin as KVCoordinate | undefined
    const scanDate = recordData?.scanDate as number | 0
    const underlyingAsset = recordData?.underlyingAsset as string | ''
    const photosEnabled = parseBoolean(recordData?.photosEnabled) ?? false
    const inspectionType =
      (recordData?.inspection_type as KestrelInspectionType | undefined) ??
      KestrelInspectionType.DEFAULT

    const boundingBox = recordData?.boundingBox
      ? KVBoundingBox.fromJSON(recordData.boundingBox as KVBoundingBoxJSON)
      : undefined

    const boundingSegments = recordData?.boundingSegments as WorldPosition[]

    // Attempt to read a default measurement unit from rolodex - undefined if not found
    const defaultUnit = normalizeKVUnit(
      (recordData as Record<string, unknown>)?.defaultUnit
    )

    // Initialize default values for optional fields
    let g1n5: G1N5 = new G1N5()
    let g1n6: G1N6 = new G1N6()
    let sceneSettings: KVSceneSettings = new KVSceneSettings()
    let dataLayersConfig: KVDataLayersConfig = new KVDataLayersConfig()

    if (recordData.annotations) {
      const annotationJSONs = recordData.annotations as Record<
        string,
        unknown
      >[]
      for (const annotation of annotationJSONs) {
        annotations.push(KVAnnotation.fromJSON(annotation))
      }
    }

    if (recordData.measurements) {
      const measurementJSONs = recordData.measurements as KVMeasurementJSON[]
      for (const measurement of measurementJSONs) {
        measurements.push(KVMeasurement.fromJSON(measurement))
      }
    }

    if (recordData.planeIntersections) {
      const planeIntersectionJSONs =
        recordData.planeIntersections as KVPlaneIntersectionJSON[]
      for (const planeIntersection of planeIntersectionJSONs) {
        planeIntersections.push(KVPlaneIntersection.fromJSON(planeIntersection))
      }
    }

    if (recordData.inspectionAreas) {
      const inspectionAreaJSONs = recordData.inspectionAreas as Record<
        string,
        unknown
      >[]
      for (const inspectionArea of inspectionAreaJSONs) {
        inspectionAreas.push(KVInspectionArea.fromJSON(inspectionArea))
      }
    }

    if (recordData.primitives) {
      const primitiveJSONs = recordData.primitives as Record<string, unknown>[]
      for (const primitive of primitiveJSONs) {
        primitives.push(KVPrimitive.fromJSON(primitive))
      }
    }

    if (recordData.g1n5) {
      g1n5 = G1N5.fromJSON(recordData.g1n5 as Record<string, unknown>)
    }

    if (recordData.g1n6) {
      g1n6 = G1N6.fromJSON(recordData.g1n6)
    }

    if (recordData.cameraViews) {
      const cameraViewJSONs = recordData.cameraViews as KVCameraViewJSON[]
      for (const cameraView of cameraViewJSONs) {
        cameraViews.push(KVCameraView.fromJSON(cameraView))
      }
    }

    if (recordData.sceneSettings) {
      sceneSettings = KVSceneSettings.fromJSON(recordData.sceneSettings)
    }

    if (recordData.dataLayersConfig) {
      dataLayersConfig = KVDataLayersConfig.fromJSON(
        recordData.dataLayersConfig as KVDataLayersConfigJSON
      )
    }

    const cameraPosesScale =
      (recordData?.cameraPosesScale as number | undefined) ?? 0.01

    const forms = recordData?.forms as Record<string, string> | undefined

    const photoRotations = recordData?.photoRotations as
      | Record<string, number>
      | undefined

    const aerospaceMetadata = recordData?.aerospaceMetadata as
      | KVAerospaceMetadata
      | undefined

    super(
      json,
      {
        kestrel_model_id: (recordData.kestrel_model_id ?? '') as string,
        inspectionAreas: inspectionAreas,
        annotations: annotations,
        measurements: measurements,
        cameraViews: cameraViews,
        planeIntersections: planeIntersections,
        primitives: primitives,
        measurementScale: measurementScale,
        photosEnabled: photosEnabled,
        g1n5: g1n5,
        g1n6: g1n6,
        inspection_type: inspectionType,
        sceneSettings: sceneSettings,
        dataLayersConfig: dataLayersConfig,
        origin: origin,
        scanDate: scanDate,
        underlyingAsset: underlyingAsset,
        boundingBox: boundingBox,
        defaultUnit: defaultUnit,
        cameraPosesScale: cameraPosesScale,
        boundingSegments: boundingSegments,
        forms: forms,
        photoRotations: photoRotations,
        aerospaceMetadata: aerospaceMetadata,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit) ?? '',
        component: parseString(json.tags.component) ?? '',
        camerasEntry: parseString(json.tags['kestrel-model-cameras']) ?? '',
      },
      {}
    )
  }

  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoKestrelModel(json)
  }
}
